import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  type TooltipValueType,
} from "recharts";
import type { T_ChartData } from "@lattice/frontend/types/charts";
import { isMultiSeries, getSeriesNames } from "@lattice/frontend/types/charts";
import {
  AXIS_COLOR,
  CHART_COLORS,
  GRID_COLOR,
  TOOLTIP_STYLE,
  TOOLTIP_LABEL_STYLE,
  TOOLTIP_ITEM_STYLE,
  formatLargeNumber,
} from "./chart-theme";

export const BarChartComponent = ({ data }: { data: T_ChartData }) => {
  const multiSeries = isMultiSeries(data);
  const seriesNames = getSeriesNames(data);

  return (
    <div className="h-72 w-full min-w-0">
      <ResponsiveContainer
        width="100%"
        height={288}
        minWidth={240}
        minHeight={288}
      >
        <BarChart
          data={data.data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={GRID_COLOR}
            vertical={false}
          />
          <XAxis
            dataKey="label"
            stroke={AXIS_COLOR}
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            stroke={AXIS_COLOR}
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatLargeNumber}
            domain={[
              (dataMin: number) => Math.min(0, dataMin),
              (dataMax: number) => Math.max(0, dataMax),
            ]}
            padding={{ top: 10, bottom: 10 }}
          />
          <Tooltip
            cursor={{ fill: "hsl(var(--muted))", opacity: 0.1 }}
            contentStyle={TOOLTIP_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
            itemStyle={!multiSeries ? TOOLTIP_ITEM_STYLE : undefined}
            wrapperStyle={{ zIndex: 1000 }}
            allowEscapeViewBox={{ x: true, y: true }}
            formatter={(value?: TooltipValueType) =>
              typeof value === "number" ? formatLargeNumber(value) : undefined
            }
          />
          {multiSeries && (
            <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" />
          )}
          {multiSeries ? (
            // Multi-series: render grouped bars
            seriesNames.map((name, i) => (
              <Bar
                key={name}
                dataKey={name}
                fill={CHART_COLORS[i % CHART_COLORS.length]}
                radius={[4, 4, 0, 0]}
                name={name}
              />
            ))
          ) : (
            // Single series: render with per-bar colors
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {data.data.map((_entry, index: number) => (
                <Cell
                  key={`cell-${index}`}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                />
              ))}
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
